/**
 * Play Project SDK Integration for React
 *
 * Provides:
 * - PlayProjectProvider: Context provider with SDK client
 * - usePlayProject: Hook to access the SDK client
 * - React Query hooks for all SDK methods
 */

import { PlayProjectClient, PlayProjectError } from '@playproject/sdk';
import type {
  Collection,
  CollectionFilters,
  CollectionSort,
  CollectionStats,
  IndexedEvent,
  MintParams,
  Nft,
  NftFilters,
  NftWithCollection,
  PaginatedResponse,
  PaginationParams,
  RegisterCollectionParams,
  UnverifyCollectionParams,
  UserCollectionNftCounts,
  UserNftsFilters,
  UserRevealParams,
  UserStats,
  VerifyCollectionParams,
  WithdrawFeesParams,
  WithdrawParams,
} from '@playproject/sdk';
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useBrand } from '../brand/context';

// =============================================================================
// Context & Provider
// =============================================================================

const PlayProjectContext = createContext<PlayProjectClient | null>(null);

/**
 * Build a QueryClient pre-configured with Play Project defaults:
 * - Centralised error logging via QueryCache onError (#635)
 * - Retry skip for non-retryable PlayProjectError (404, 400)
 * - 60s staleTime baseline, refetchOnWindowFocus disabled for Telegram WebView
 *
 * Each call returns a fresh client, so PlayProjectProvider creates per-mount
 * isolation (fixes #633 test pollution and HMR cache leaks). Callers may
 * construct their own and pass it to PlayProjectProvider via the queryClient
 * prop — useful for tests (inject `retry: false`) or Storybook.
 */
export function createPlayProjectQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (error instanceof PlayProjectError) {
          console.error('[PlayProject] query failed', {
            key: query.queryKey,
            status: error.status,
            code: error.code,
            message: error.message,
            retryAfter: error.retryAfterSeconds,
          });
        } else {
          console.error('[PlayProject] query failed', { key: query.queryKey, error });
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        retry: (failureCount, error) => {
          if (error instanceof PlayProjectError && !error.isRetryable) return false;
          return failureCount < 2;
        },
        refetchOnWindowFocus: false,
      },
    },
  });
}

export interface PlayProjectProviderProps {
  /** Indexer API URL */
  indexerUrl: string;
  /** TonAPI key for blockchain queries (optional, for enhanced API access) */
  tonApiKey?: string;
  /** API key for indexer authentication */
  apiKey?: string;
  /** Network: 'mainnet' or 'testnet' (default: 'testnet') */
  network?: 'mainnet' | 'testnet';
  /**
   * Optional QueryClient override. Defaults to a fresh client from
   * createPlayProjectQueryClient(). Tests should inject their own to keep
   * caches isolated across test files.
   */
  queryClient?: QueryClient;
  /** Children components */
  children: ReactNode;
}

export function PlayProjectProvider({
  indexerUrl,
  tonApiKey,
  apiKey,
  network = 'testnet',
  queryClient,
  children,
}: PlayProjectProviderProps) {
  const client = useMemo(
    () =>
      new PlayProjectClient({
        baseUrl: indexerUrl,
        tonApiKey,
        apiKey,
        network,
        // Vite replaces import.meta.env.DEV with a literal true/false at build
        // time, so no runtime `process.env` reference survives in the bundle.
        validateResponses: import.meta.env.DEV,
      }),
    [indexerUrl, tonApiKey, apiKey, network]
  );

  // useState lazy init gives a stable client for the provider's lifetime
  // without the recompute risk that useMemo has under React's discard model.
  const [internalQueryClient] = useState(() => queryClient ?? createPlayProjectQueryClient());

  return (
    <QueryClientProvider client={internalQueryClient}>
      <PlayProjectContext.Provider value={client}>{children}</PlayProjectContext.Provider>
    </QueryClientProvider>
  );
}

export function usePlayProject(): PlayProjectClient {
  const client = useContext(PlayProjectContext);
  if (!client) {
    throw new Error('usePlayProject must be used within a PlayProjectProvider');
  }
  return client;
}

/**
 * Hook to access SDK configuration
 * Useful for displaying network info, debugging, or conditional rendering
 */
export function usePlayProjectConfig() {
  const client = usePlayProject();
  return client.getConfig();
}

// =============================================================================
// Query Keys
// =============================================================================

export const playProjectKeys = {
  all: ['playproject'] as const,
  collections: () => [...playProjectKeys.all, 'collections'] as const,
  collectionsList: (
    filters?: CollectionFilters,
    sort?: CollectionSort,
    pagination?: PaginationParams
  ) => [...playProjectKeys.collections(), 'list', { filters, sort, pagination }] as const,
  collection: (address: string) => [...playProjectKeys.collections(), 'detail', address] as const,
  collectionStats: (address: string) => [...playProjectKeys.collection(address), 'stats'] as const,
  collectionNfts: (address: string, filters?: NftFilters, pagination?: PaginationParams) =>
    [...playProjectKeys.collection(address), 'nfts', { filters, pagination }] as const,
  collectionEvents: (address: string, type?: string, pagination?: PaginationParams) =>
    [...playProjectKeys.collection(address), 'events', { type, pagination }] as const,
  nfts: () => [...playProjectKeys.all, 'nfts'] as const,
  nft: (address: string) => [...playProjectKeys.nfts(), 'detail', address] as const,
  nftHistory: (address: string, pagination?: PaginationParams) =>
    [...playProjectKeys.nft(address), 'history', { pagination }] as const,
  // reveal nft here
  users: () => [...playProjectKeys.all, 'users'] as const,
  user: (address: string) => [...playProjectKeys.users(), address] as const,
  userNfts: (address: string, filters?: UserNftsFilters, pagination?: PaginationParams) =>
    [...playProjectKeys.user(address), 'nfts', { filters, pagination }] as const,
  userActivity: (address: string, pagination?: PaginationParams) =>
    [...playProjectKeys.user(address), 'activity', { pagination }] as const,
  userCollections: (address: string, pagination?: PaginationParams) =>
    [...playProjectKeys.user(address), 'collections', { pagination }] as const,
  userStats: (address: string) => [...playProjectKeys.user(address), 'stats'] as const,
  userCollectionNftCounts: (address: string, collectionAddresses: string[]) =>
    [...playProjectKeys.user(address), 'nft-counts', collectionAddresses] as const,
};

// =============================================================================
// Freshness Tiers (#634)
//
// React-query defaults are intentionally conservative (60s stale, no poll).
// Each hook below picks a tier that matches its underlying data's rate of
// change, and paired hooks (e.g. useCollections / useInfiniteCollections)
// share the same tier so they don't disagree on what "fresh" means.
// =============================================================================

/** 10s stale + 10s poll — batch endpoints shown alongside rapidly-updating data. */
const FRESHNESS_REAL_TIME = { staleTime: 10_000, refetchInterval: 10_000 } as const;

/** 15s stale + 30s poll — collection list/detail; on-chain events mutate state. */
const FRESHNESS_FAST = { staleTime: 15_000, refetchInterval: 30_000 } as const;

/** 30s stale + 30s poll — user NFT snapshot; polls during active mint flows. */
const FRESHNESS_NORMAL = { staleTime: 30_000, refetchInterval: 30_000 } as const;

/** 60s stale, no poll — rarely-changing read-once data (detail pages). */
const FRESHNESS_SLOW = { staleTime: 60_000 } as const;

// =============================================================================
// Collection Hooks
// =============================================================================

/**
 * Merge caller-supplied filters with the current brand's app id (Spec 11).
 * If the caller explicitly passes `app`, that takes precedence.
 * @internal exported for tests only
 */
export function useAppScopedFilters(filters?: CollectionFilters): CollectionFilters {
  const brand = useBrand();
  if (filters?.app) return filters;
  return { ...filters, app: brand.id };
}

export function useCollections(
  filters?: CollectionFilters,
  sort?: CollectionSort,
  pagination?: PaginationParams
) {
  const client = usePlayProject();
  const scopedFilters = useAppScopedFilters(filters);
  return useQuery({
    queryKey: playProjectKeys.collectionsList(scopedFilters, sort, pagination),
    queryFn: () => client.getCollections(scopedFilters, sort, pagination),
    ...FRESHNESS_FAST,
  });
}

export function useInfiniteCollections(
  filters?: CollectionFilters,
  sort?: CollectionSort,
  itemsPerPage = 10
) {
  const client = usePlayProject();
  const scopedFilters = useAppScopedFilters(filters);
  return useInfiniteQuery({
    queryKey: [
      ...playProjectKeys.collections(),
      'infinite',
      { filters: scopedFilters, sort, itemsPerPage },
    ],
    queryFn: ({ pageParam = 1 }) =>
      client.getCollections(scopedFilters, sort, { page: pageParam, limit: itemsPerPage }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.pagination) return undefined;
      return lastPage.pagination.page < lastPage.pagination.totalPages
        ? lastPage.pagination.page + 1
        : undefined;
    },
    initialPageParam: 1,
    ...FRESHNESS_FAST,
  });
}

export function useCollection(address: string) {
  const client = usePlayProject();
  return useQuery({
    queryKey: playProjectKeys.collection(address),
    queryFn: () => client.getCollection(address),
    enabled: !!address,
    ...FRESHNESS_FAST,
  });
}

export function useCollectionStats(address: string) {
  const client = usePlayProject();
  return useQuery({
    queryKey: playProjectKeys.collectionStats(address),
    queryFn: () => client.getCollectionStats(address),
    enabled: !!address,
    ...FRESHNESS_FAST,
  });
}

export function useCollectionNfts(
  address: string,
  filters?: NftFilters,
  pagination?: PaginationParams
) {
  const client = usePlayProject();
  return useQuery({
    queryKey: playProjectKeys.collectionNfts(address, filters, pagination),
    queryFn: () => client.getCollectionNfts(address, filters, pagination),
    enabled: !!address,
    ...FRESHNESS_SLOW,
  });
}

export function useCollectionEvents(address: string, type?: string, pagination?: PaginationParams) {
  const client = usePlayProject();
  return useQuery({
    queryKey: playProjectKeys.collectionEvents(address, type, pagination),
    queryFn: () => client.getCollectionEvents(address, type, pagination),
    enabled: !!address,
    ...FRESHNESS_SLOW,
  });
}

// =============================================================================
// NFT Hooks
// =============================================================================

export function useNft(address: string) {
  const client = usePlayProject();
  return useQuery({
    queryKey: playProjectKeys.nft(address),
    queryFn: () => client.getNft(address),
    enabled: !!address,
    ...FRESHNESS_SLOW,
  });
}

export function useNftHistory(address: string, pagination?: PaginationParams) {
  const client = usePlayProject();
  return useQuery({
    queryKey: playProjectKeys.nftHistory(address, pagination),
    queryFn: () => client.getNftHistory(address, pagination),
    enabled: !!address,
    ...FRESHNESS_SLOW,
  });
}

// =============================================================================
// User Hooks
// =============================================================================

export function useUserNfts(
  address: string,
  filters?: UserNftsFilters,
  pagination?: PaginationParams
) {
  const client = usePlayProject();
  return useQuery({
    queryKey: playProjectKeys.userNfts(address, filters, pagination),
    queryFn: () => client.getUserNfts(address, filters, pagination),
    enabled: !!address,
    ...FRESHNESS_NORMAL,
  });
}

/**
 * Hook for infinite scroll user NFTs using React Query's useInfiniteQuery
 * Automatically manages pagination
 */
export function useInfiniteUserNfts(
  address: string,
  filters?: UserNftsFilters,
  itemsPerPage = 100
) {
  const client = usePlayProject();
  return useInfiniteQuery({
    queryKey: [...playProjectKeys.user(address), 'nfts', 'infinite', { filters, itemsPerPage }],
    queryFn: ({ pageParam = 1 }) =>
      client.getUserNfts(address, filters, { page: pageParam, limit: itemsPerPage }),
    getNextPageParam: (lastPage) => {
      if (!lastPage.pagination) return undefined;
      return lastPage.pagination.page < lastPage.pagination.totalPages
        ? lastPage.pagination.page + 1
        : undefined;
    },
    initialPageParam: 1,
    enabled: !!address,
    ...FRESHNESS_NORMAL,
  });
}

export function useUserActivity(address: string, pagination?: PaginationParams) {
  const client = usePlayProject();
  return useQuery({
    queryKey: playProjectKeys.userActivity(address, pagination),
    queryFn: () => client.getUserActivity(address, pagination),
    enabled: !!address,
    ...FRESHNESS_SLOW,
  });
}

export function useUserCollections(address: string, pagination?: PaginationParams) {
  const client = usePlayProject();
  return useQuery({
    queryKey: playProjectKeys.userCollections(address, pagination),
    queryFn: () => client.getUserCollections(address, pagination),
    enabled: !!address,
    ...FRESHNESS_SLOW,
  });
}

export function useUserStats(address: string) {
  const client = usePlayProject();
  return useQuery({
    queryKey: playProjectKeys.userStats(address),
    queryFn: () => client.getUserStats(address),
    enabled: !!address,
    ...FRESHNESS_SLOW,
  });
}

export function useUserCollectionNftCounts(userAddress: string, collectionAddresses: string[]) {
  const client = usePlayProject();
  return useQuery({
    queryKey: playProjectKeys.userCollectionNftCounts(userAddress, collectionAddresses),
    queryFn: () => client.getUserCollectionNftCounts(userAddress, collectionAddresses),
    enabled: !!userAddress && collectionAddresses.length > 0,
    ...FRESHNESS_REAL_TIME,
  });
}

// =============================================================================
// Transaction Hooks (return transaction payloads for TonConnect)
//
// #636: These wrap pure-sync builders on the SDK client, so they return the
// builder function directly instead of a useMutation wrapper. Callers previously
// destructured `{ mutateAsync }`; they now use the returned function directly.
// `await fn(params)` continues to work because JS auto-wraps non-Promise values.
// =============================================================================

export function useMintTransaction() {
  const client = usePlayProject();
  return useCallback((params: MintParams) => client.createMintTransaction(params), [client]);
}

export function useUserRevealTransaction() {
  const client = usePlayProject();
  return useCallback(
    (params: UserRevealParams) => client.createUserRevealTransaction(params),
    [client]
  );
}

export function useWithdrawTransaction() {
  const client = usePlayProject();
  return useCallback(
    (params: WithdrawParams) => client.createWithdrawTransaction(params),
    [client]
  );
}

export function useRegisterCollectionTransaction() {
  const client = usePlayProject();
  return useCallback(
    (params: RegisterCollectionParams) => client.createRegisterCollectionTransaction(params),
    [client]
  );
}

export function useVerifyCollectionTransaction() {
  const client = usePlayProject();
  return useCallback(
    (params: VerifyCollectionParams) => client.createVerifyCollectionTransaction(params),
    [client]
  );
}

export function useUnverifyCollectionTransaction() {
  const client = usePlayProject();
  return useCallback(
    (params: UnverifyCollectionParams) => client.createUnverifyCollectionTransaction(params),
    [client]
  );
}

export function useWithdrawFeesTransaction() {
  const client = usePlayProject();
  return useCallback(
    (params: WithdrawFeesParams) => client.createWithdrawFeesTransaction(params),
    [client]
  );
}

// =============================================================================
// Invalidation Helpers
// =============================================================================

export function useInvalidateCollection() {
  const queryClient = useQueryClient();
  return (address: string) => {
    queryClient.invalidateQueries({
      queryKey: playProjectKeys.collection(address),
    });
  };
}

export function useInvalidateUserData() {
  const queryClient = useQueryClient();
  return (address: string) => {
    queryClient.invalidateQueries({ queryKey: playProjectKeys.user(address) });
  };
}

export function useInvalidateAllCollections() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: playProjectKeys.collections() });
  };
}

// =============================================================================
// Re-export types for convenience
// =============================================================================

export type {
  Collection,
  CollectionFilters,
  CollectionSort,
  CollectionStats,
  IndexedEvent,
  Nft,
  NftWithCollection,
  NftFilters,
  PaginationParams,
  PaginatedResponse,
  MintParams,
  UserRevealParams,
  WithdrawParams,
  RegisterCollectionParams,
  VerifyCollectionParams,
  UnverifyCollectionParams,
  WithdrawFeesParams,
  UserNftsFilters,
  UserStats,
  UserCollectionNftCounts,
};

// Re-export utility methods
export { PlayProjectClient };
